import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, MapPin, ChevronRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import FAQAccordion from "@/components/FAQAccordion";
import { useLanguage } from "@/context/LanguageContext";
import { useSeoHead } from "@/lib/useSeoHead";
import { SEO_REGION_MAP } from "@/lib/seoRegions";
import { SEO_CITY_MAP } from "@/lib/seoCities";
import { SEO_STYLES } from "@/lib/seoStyles";
import { trackScan } from "@/lib/api";

const ORIGIN = "https://feaststar.app";

/**
 * Region hub landing (e.g. /region/allgaeu, /region/bayern).
 * Aggregates all cities in a region + region-specific FAQs. Strong AEO
 * signal for regional long-tail queries like "Bio-Restaurants Allgäu".
 */
export default function RegionLanding() {
  const { region } = useParams();
  const { lang, t } = useLanguage();

  const regionData = SEO_REGION_MAP[region];
  const valid = !!regionData;

  const seo = useMemo(() => {
    if (!valid) return null;
    const nm = lang === "de" ? regionData.nameDe : regionData.nameEn;
    const hero = lang === "de" ? regionData.heroDe : regionData.heroEn;
    const intro = lang === "de" ? regionData.introDe : regionData.introEn;
    const whatIs = lang === "de" ? regionData.whatIsDe : regionData.whatIsEn;
    const faqsSrc = lang === "de" ? regionData.faqsDe : regionData.faqsEn;
    const keywords = lang === "de" ? regionData.keywordsDe : regionData.keywordsEn;

    const canonical = `${ORIGIN}/region/${regionData.slug}`;
    const titleDe = `Bio, regional & bewusst essen in ${nm} — Feaststar`;
    const titleEn = `Organic, regional & mindful dining in ${nm} — Feaststar`;
    const descDe = `Restaurants, Cafés, Bäckereien und Hofläden in der Region ${nm} — gefiltert nach Bio-Standard, Zutatenherkunft, Frische und echten Community-Bewertungen. Feaststar bündelt die bewusste Food-Szene ${nm}s auf einer Karte.`;
    const descEn = `Restaurants, cafés, bakeries and farm shops in the ${nm} region — filtered by organic standards, ingredient origin, freshness and real community ratings. Feaststar bundles the mindful food scene of ${nm}.`;

    // JSON-LD: Place + FAQPage + Breadcrumb
    const faqJsonLd = faqsSrc.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    }));

    const breadcrumb = [
      { "@type": "ListItem", position: 1, name: lang === "de" ? "Startseite" : "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: nm, item: canonical },
    ];

    return {
      title: lang === "de" ? titleDe : titleEn,
      description: lang === "de" ? descDe : descEn,
      keywords,
      canonical,
      hero,
      intro,
      whatIs,
      faqs: faqsSrc,
      alternates: [
        { hreflang: "de", href: canonical },
        { hreflang: "de-DE", href: canonical },
        { hreflang: "en", href: `${canonical}?lang=en` },
        { hreflang: "x-default", href: canonical },
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Place",
          name: nm,
          description: intro,
          address: {
            "@type": "PostalAddress",
            addressRegion: regionData.parentRegion,
            addressCountry: regionData.country,
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqJsonLd,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumb,
        },
      ],
    };
  }, [valid, regionData, lang]);

  useSeoHead(
    valid
      ? {
          title: seo.title,
          description: seo.description,
          keywords: seo.keywords,
          canonical: seo.canonical,
          ogType: "website",
          alternates: seo.alternates,
          jsonLd: seo.jsonLd,
        }
      : { title: "Region nicht gefunden | Feaststar", canonical: `${ORIGIN}/` },
  );

  useEffect(() => {
    if (!valid) return;
    trackScan({ page: `region-${regionData.slug}`, language: lang });
  }, [valid, regionData, lang]);

  if (!valid) return <Navigate to="/" replace />;

  const cityObjects = regionData.citySlugs
    .map((slug) => SEO_CITY_MAP[slug])
    .filter(Boolean);
  const nm = lang === "de" ? regionData.nameDe : regionData.nameEn;

  // Feature the 3 most-searched styles for region hubs: bio, glutenfrei, regional
  const highlightStyles = SEO_STYLES.filter((s) =>
    ["bio", "glutenfrei", "regional"].includes(s.slug),
  );

  return (
    <div className="min-h-screen bg-bg" data-testid={`region-page-${regionData.slug}`}>
      <Nav variant="home" />

      {/* HERO */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 body-sm text-muted mb-6">
            <MapPin size={14} strokeWidth={1.5} className="text-accent" />
            <span>{regionData.parentRegion}</span>
          </div>
          <h1 className="display-lg lg:display-xl text-primary max-w-[900px] mx-auto">
            <span className="block">{seo.hero.a}</span>
            <span className="block italic text-accent">{seo.hero.b}</span>
          </h1>
          <p className="body-md mt-8 max-w-[720px] mx-auto text-muted">{seo.intro}</p>
        </div>
      </section>

      {/* CITIES GRID */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="mb-10">
            <div className="form-label text-accent mb-3">
              {lang === "de" ? "Städte in der Region" : "Cities in this region"}
            </div>
            <h2 className="display-md text-primary">
              <span className="block">
                {lang === "de" ? `${cityObjects.length} Städte in ${nm}.` : `${cityObjects.length} cities in ${nm}.`}
              </span>
              <span className="block italic text-accent">
                {lang === "de" ? "Ein Anspruch." : "One standard."}
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cityObjects.map((c) => {
              const cName = lang === "de" ? c.nameDe : c.nameEn;
              const cHook = lang === "de" ? c.localHookDe : c.localHookEn;
              return (
                <Link
                  key={c.slug}
                  to={`/bio/${c.slug}`}
                  data-testid={`region-city-card-${c.slug}`}
                  className="card-standard group hover:border-accent transition-colors block"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="form-label text-muted mb-1">{c.region}</div>
                      <h3 className="font-display text-2xl text-primary leading-tight">{cName}</h3>
                    </div>
                    <ChevronRight size={18} className="text-accent opacity-40 group-hover:opacity-100 transition-opacity mt-2" />
                  </div>
                  <p className="body-sm text-muted line-clamp-3">{cHook}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT IS */}
      <section className="pb-20 lg:pb-28 border-t border-soft">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24">
          <div className="form-label text-accent mb-3">
            {lang === "de" ? `Was ${nm} kulinarisch ausmacht` : `What defines ${nm} culinarily`}
          </div>
          <h2 className="display-md text-primary mb-6">
            <span className="block">{lang === "de" ? nm : nm}</span>
            <span className="block italic text-accent">
              {lang === "de" ? "im Detail." : "in detail."}
            </span>
          </h2>
          <p className="body-md text-primary">{seo.whatIs}</p>
        </div>
      </section>

      {/* POPULAR STYLES × CITIES */}
      <section className="pb-20 lg:pb-28 border-t border-soft">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24">
          <div className="form-label text-accent mb-3">
            {lang === "de" ? "Beliebteste Kombinationen" : "Most popular combinations"}
          </div>
          <h2 className="display-md text-primary mb-10">
            <span className="block">
              {lang === "de" ? `Beliebte Suchen in ${nm}.` : `Popular searches in ${nm}.`}
            </span>
          </h2>

          <div className="space-y-8">
            {highlightStyles.map((style) => (
              <div key={style.slug}>
                <h3 className="font-display text-xl text-primary mb-3">
                  {lang === "de" ? style.shortDe : style.shortEn}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cityObjects.map((c) => (
                    <Link
                      key={`${style.slug}-${c.slug}`}
                      to={`/${style.slug}/${c.slug}`}
                      data-testid={`region-tag-${style.slug}-${c.slug}`}
                      className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-accent rounded-full text-primary hover:bg-accent hover:text-background transition-colors"
                    >
                      {lang === "de" ? style.shortDe : style.shortEn}{" "}
                      <span className="text-accent group-hover:text-background">
                        {lang === "de" ? c.nameDe : c.nameEn}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 lg:pb-28 border-t border-soft">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24">
          <div className="form-label text-accent mb-3">FAQ</div>
          <h2 className="display-md text-primary mb-10">
            <span className="block">
              {lang === "de" ? `Häufige Fragen zu ${nm}.` : `Common questions about ${nm}.`}
            </span>
          </h2>
          <FAQAccordion items={seo.faqs} />
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 lg:pb-32 border-t border-soft">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24 text-center">
          <h2 className="display-md text-primary mb-4">
            <span className="block">{lang === "de" ? "Feaststar" : "Feaststar"}</span>
            <span className="block italic text-accent">
              {lang === "de" ? `startet bald in ${nm}.` : `launches soon in ${nm}.`}
            </span>
          </h2>
          <p className="body-md text-muted mb-8">
            {lang === "de"
              ? "Sei bei der ersten Version dabei — keine Newsletter-Flut, nur die eine Launch-Nachricht."
              : "Be there for the first version — no newsletter flood, just the one launch message."}
          </p>
          <Link
            to="/"
            data-testid="region-cta-home"
            className="btn-primary inline-flex items-center gap-2"
          >
            {lang === "de" ? "Zur Startseite" : "Back to home"}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer variant="home" />
    </div>
  );
}
